import { useCallback, useMemo, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';
import { Grid } from 'semantic-ui-react';

import { useConceptLabelFormatter } from 'reducers/entityLabelFormatter';
import { leavesConceptKpisSelector } from 'selectors/view';

import PrioritizationScatterChart from 'components/customer/visualization/scatter-chart/PrioritizationScatterChart';
import ScatterChartWithExportModal from 'components/customer/visualization/scatter-chart/ScatterChartWithExportModal';
import BeatingLoader from 'components/ui/BeatingLoader';
import EmptyDataPage from 'components/ui/EmptyDataPage';
import { NormalSpacePaddedRow } from 'components/ui/Grid';
import HelpTooltip from 'components/ui/HelpTooltip';
import ButtonGroup from 'components/ui/button/ButtonGroup';
import { SecondaryTabButton } from 'components/ui/button/TabButton';
import {
  NonExcelExportAsXlsIcon,
  exportAsXls,
} from 'components/ui/button/export-as/ExportAsXls';
import {
  ChartMenu,
  ChartSegment,
  HeaderContainer,
  TitleSpan,
} from 'components/ui/dashboard/DashboardElement';
import { AnalyticsAwareCheckbox } from 'components/ui/inputs/Checkbox';
import emptyDataUrl from 'components/ui/svg/undraw_circles_y7s2.svg';
import { useReactTableColumns } from 'components/ui/table/ReactTableColumns';
import ReactTableWithExportModal from 'components/ui/table/ReactTableWithExportModal';
import {
  SENTIMENT_SCORE_DOMAIN_STEP,
  getAdaptativeDomain,
} from 'components/ui/visualization/domain';

import config from 'config';
import commonPropTypes from 'utils/commonPropTypes';
import { useMemoizedFactorySelector } from 'utils/hooks';
import capitalizedTranslation from 'utils/i18n';
import {
  PRIORITIZATION_CATEGORY_ALL,
  PRIORITIZATION_CATEGORY_ITEMS,
  SENTIMENT_POLARITY_ITEMS_ALL,
  getPriorityCategory,
} from 'utils/types';

import * as svars from 'assets/style/variables';

const PRIORITIZATION_CHART_HEIGHT = 362;
const PRIORITIZATION_SORTED = [{ id: 'priority', desc: true }];

const getColumnsMap = (withReviewScore) => ({
  concept: { id: 'name', accessor: 'name' },
  n_chunks: { id: 'n_chunks' },
  average_sentiment: { id: 'sentiment' },
  ...(withReviewScore ? { average_score: { id: 'average_score' } } : {}),
  priority: { id: 'priority' },
});

const TOP_BOTTOM_ABSOLUTE_MIN_N_CHUNKS = 20;
const TOP_BOTTOM_ABSOLUTE_MIN_SENTIMENT = 0.05;
// Set top/bottom candidates to a high value to display all candidates
// const TOP_BOTTOM_ONTOLOGY_MAX_CANDIDATES = 50;

const filterConceptKpi = (priorityCategory) => (item) =>
  item?.n_chunks >= TOP_BOTTOM_ABSOLUTE_MIN_N_CHUNKS &&
  Math.abs(item?.average_sentiment) >= TOP_BOTTOM_ABSOLUTE_MIN_SENTIMENT &&
  (priorityCategory === 'all' ||
    (priorityCategory === 'positives' && item?.average_sentiment > 0) ||
    (priorityCategory === 'negatives' && item?.average_sentiment < 0) ||
    getPriorityCategory(item) === priorityCategory);

const getDisabledCategories = (conceptKpis) => {
  const disabledCategories = {};
  PRIORITIZATION_CATEGORY_ITEMS.forEach(({ value }) => {
    const hasData = conceptKpis.some(filterConceptKpi(value));
    disabledCategories[value] = !hasData;
  });
  return disabledCategories;
};

function ConceptPrioritization({ viewFacet, loading }) {
  const { i18n } = useLingui();
  const [leavesOnly, setLeavesOnly] = useState(true);
  const [scatterExportModalIsOpen, setScatterExportModalIsOpen] =
    useState(false);
  const [tableExportModalIsOpen, setTableExportModalIsOpen] = useState(false);
  const [tablePrioritizationCategory, setTablePrioritizationCategory] =
    useState(PRIORITIZATION_CATEGORY_ALL);
  const [exportName, setExportName] = useState('concept-prioritization');
  const onTablePrioritizationCategoryChange = useCallback(
    (e, { value: category }) => setTablePrioritizationCategory(category),
    []
  );
  const toggleTableExportModal = useCallback(
    () => setTableExportModalIsOpen(!tableExportModalIsOpen),
    [tableExportModalIsOpen]
  );
  const toggleScatterExportModal = useCallback(
    () => setScatterExportModalIsOpen(!scatterExportModalIsOpen),
    [scatterExportModalIsOpen]
  );

  const navigate = useNavigate();
  const conceptLabelFormatter = useConceptLabelFormatter();
  const navigateToExploration = useCallback(
    (conceptId) => {
      navigate({
        pathname: `../analyze/${viewFacet?.id}/explore`,
        search: createSearchParams({
          concept: conceptId,
        }).toString(),
      });
    },
    [navigate, viewFacet]
  );
  const conceptKpis = useMemoizedFactorySelector(
    leavesConceptKpisSelector,
    true,
    null,
    leavesOnly
  );
  const [filteredConceptKpis, nMaxChunks] = useMemo(
    () => [
      conceptKpis
        .filter(filterConceptKpi(tablePrioritizationCategory))
        .sort((a, b) => b.priority - a.priority),
      // .slice(0, TOP_BOTTOM_ONTOLOGY_MAX_CANDIDATES),
      Math.max(...conceptKpis.map((item) => item.n_chunks), 0),
    ],
    [conceptKpis, tablePrioritizationCategory]
  );

  const disabledCategories = useMemo(
    () => getDisabledCategories(conceptKpis),
    [conceptKpis]
  );

  const columns = useMemo(() => {
    const maxNChunks = Math.max(
      ...filteredConceptKpis.map((item) => item.n_chunks)
    );

    return useReactTableColumns(
      getColumnsMap(viewFacet.use_review_score),
      maxNChunks,
      0,
      viewFacet.use_review_score
    );
  }, [filteredConceptKpis]);

  const exportDataset = () =>
    exportAsXls(
      filteredConceptKpis,
      columns,
      null,
      t({ id: 'concept-prioritization' })
    );

  const onShapeClick = useCallback(
    (event, { id: newConceptId }) => {
      navigateToExploration(newConceptId);
    },
    [navigateToExploration]
  );
  const onTableRowClick = useCallback(
    (row) => navigateToExploration(row.db_concept),
    [navigateToExploration]
  );

  const prioritizationCategories = useMemo(
    () =>
      (viewFacet.use_review_score
        ? PRIORITIZATION_CATEGORY_ITEMS
        : SENTIMENT_POLARITY_ITEMS_ALL
      ).map((item) => ({
        ...item,
        disabled: disabledCategories[item.value],
      })),
    [viewFacet.use_review_score, disabledCategories]
  );
  const chartProps = {
    data: filteredConceptKpis,
    tablePrioritizationCategory,
    labelFormatter: conceptLabelFormatter,
    onShapeClick,
    categoryDataKey: 'db_concept',
    isAnimationActive: false,
    cellKey: 'db_concept',
  };
  const xDomain = useMemo(
    () =>
      getAdaptativeDomain(
        config.SENTIMENT_DOMAIN,
        SENTIMENT_SCORE_DOMAIN_STEP,
        filteredConceptKpis,
        'average_sentiment'
      ),
    [filteredConceptKpis]
  );
  if (loading) {
    return (
      <ChartSegment style={{ height: svars.prioritizationChartHeight }}>
        <BeatingLoader />
      </ChartSegment>
    );
  }
  if (!filteredConceptKpis?.length) {
    return (
      <ChartSegment>
        <EmptyDataPage
          layout="row"
          headerText={<Trans id="concept-prioritization.not-enough-data" />}
          actionComponent={
            <Trans id="concept-prioritization.not-enough-data-body" />
          }
          illustrationUrl={emptyDataUrl}
        />
      </ChartSegment>
    );
  }
  return (
    <NormalSpacePaddedRow
      columns={2}
      style={{
        paddingTop: 0,
      }}
    >
      <Grid.Column width={7} stretched style={{ padding: 0 }}>
        <ChartSegment>
          <HeaderContainer>
            <TitleSpan>
              <span>
                <Trans
                  render={capitalizedTranslation}
                  id="prioritization-matrix"
                />
              </span>
            </TitleSpan>
            <ChartMenu
              exportAsImage={toggleScatterExportModal}
              exportDataAsXls={exportDataset}
            />
          </HeaderContainer>
          {viewFacet.use_review_score ? (
            <PrioritizationScatterChart
              nMaxChunks={nMaxChunks}
              modalIsOpen={scatterExportModalIsOpen}
              onCloseModal={toggleScatterExportModal}
              {...chartProps}
            />
          ) : (
            <ScatterChartWithExportModal
              getRadius={svars.getPercentScatterRadius('share_of_extracts')}
              useSentimentColor
              height={svars.prioritizationChartHeight}
              xDomain={xDomain}
              modalIsOpen={scatterExportModalIsOpen}
              onCloseModal={toggleScatterExportModal}
              {...chartProps}
            />
          )}
        </ChartSegment>
      </Grid.Column>
      <Grid.Column
        width={9}
        stretched
        style={{ padding: 0 }}
        data-testid="action-priorization-table"
      >
        <ChartSegment style={{ paddingBottom: 0 }}>
          <HeaderContainer data-testid="header-container">
            <TitleSpan>
              <span>
                <Trans
                  render={capitalizedTranslation}
                  id="actions-prioritization"
                />
              </span>
            </TitleSpan>
            <ChartMenu
              ExportTableIcon={NonExcelExportAsXlsIcon}
              exportDataAsXls={toggleTableExportModal}
            />
          </HeaderContainer>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: `${PRIORITIZATION_CHART_HEIGHT}px`,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'baseline' }}>
              <ButtonGroup
                as={SecondaryTabButton}
                items={prioritizationCategories}
                style={{
                  margin: `0 ${svars.spaceNormal}`,
                  paddingBottom: svars.spaceNormalLarge,
                  flexShrink: 0,
                  flexWrap: 'nowrap',
                }}
                onChange={onTablePrioritizationCategoryChange}
                value={tablePrioritizationCategory}
                buttonBottomPadded
              />
              <HelpTooltip
                help={<Trans id="help-message.parent-category-description" />}
                size="tiny"
                position="top center"
                trigger={
                  <AnalyticsAwareCheckbox
                    gaCategory="Customer"
                    gaAction="Analyze - toggle parents nodes"
                    gaLabel={`${leavesOnly}`}
                    clicked={leavesOnly ? 1 : 0}
                    onChange={() => setLeavesOnly((prev) => !prev)}
                    style={{
                      borderRadius: 0,
                      textAlign: 'initial',
                      fontWeight: svars.fontWeightBase,
                      fontSize: svars.fontSizeSmaller,
                      margin: `0 ${svars.spaceNormal}`,
                    }}
                    label={capitalize(
                      i18n._(t({ id: 'display-also-parent-categories' }))
                    )}
                    checked={!leavesOnly}
                  />
                }
              />
            </span>
            <ReactTableWithExportModal
              data={filteredConceptKpis}
              columns={columns}
              modalIsOpen={tableExportModalIsOpen}
              onCloseModal={toggleTableExportModal}
              exportName={exportName}
              setExportName={setExportName}
              defaultSorted={PRIORITIZATION_SORTED}
              onRowClick={onTableRowClick}
            />
          </div>
        </ChartSegment>
      </Grid.Column>
    </NormalSpacePaddedRow>
  );
}

ConceptPrioritization.propTypes = {
  viewFacet: commonPropTypes.viewFacet.isRequired,
  loading: PropTypes.bool,
};

ConceptPrioritization.defaultProps = {
  loading: false,
};

export default ConceptPrioritization;
